import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ref, onValue } from 'firebase/database';
import { db, auth } from '../../../integrations/firebase';
import { useNavigate } from 'react-router-dom';
import { Phone, PhoneOff, PhoneIncoming, MessageCircle, MessageSquare, User, X, Minus, LifeBuoy, Truck, ExternalLink } from 'lucide-react';
import { useLiveCall } from '../../../core/telephony/useLiveCall';
import { usePhonePresence } from '../../../core/telephony/usePhonePresence';
import { savedElsewhere, savedLabel, OUTCOME_WAIT_MS } from '../../../core/telephony/liveCall';

// LiveCallDock — the agent's call, controlled FROM THE SYSTEM.
//
// The phone is only the handset: when it rings or dials, this dock pops up on
// every ERP page with the full customer profile, a live ticking status, and
// Attend / Hang up buttons that remote-control the phone (Daksha Caller
// v2.4+). WhatsApp opens prefilled; the profile link jumps to the customer.

const PROXY = (import.meta.env?.VITE_PROXY_URL || (import.meta.env?.DEV ? 'http://localhost:3001' : '')).replace(/\/$/, '');
const api = async (path, body) => {
    const { authHeaders } = await import('../../../core/api/authHeaders');
    const r = await fetch(`${PROXY}${path}`, {
        method: body ? 'POST' : 'GET',
        headers: { ...(body ? { 'Content-Type': 'application/json' } : {}), ...(await authHeaders()) },
        body: body ? JSON.stringify(body) : undefined,
    });
    return r.json().catch(() => ({}));
};

const fmtNiceDate = (d) => {
    const t = Date.parse(d);
    return Number.isFinite(t)
        ? new Date(t).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
        : (d || '—');
};

// A live-call entry is stale when its state has clearly outlived itself:
// a ring or dial that hasn't become a call within 2 min is over (the phone
// failed to report 'idle'); an in-call is capped at 2h. Fixes stuck 'ringing'
// strips that counted up for an hour-plus.
const liveCallStale = (c, now = Date.now()) => {
    if (!c || !c.state || c.state === 'idle') return true;
    const age = now - (c.at || 0);
    if (c.state === 'ringing' || c.state === 'dialing') return age > 2 * 60_000;
    return age > 2 * 3600_000;
};

const mmss = (ms) => {
    const s = Math.max(0, Math.floor(ms / 1000));
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

// Inline outcome + note while (or right after) the call - one click, no
// navigation. Saves as an outcome-only record against the app-reported call.
//
// Caller 4.30: once the call has ENDED the same outcome form is open on the phone
// too. `liveCallKey` identifies the call to the server, which keeps only the first
// save; `savedBy` = the other device saved first, so this form closes itself.
const QuickOutcome = ({ mobile, customerName, ended, liveCallKey = '', savedBy = '' }) => {
    const [result, setResult] = useState('');
    const [note, setNote] = useState('');
    const [followUp, setFollowUp] = useState('');
    const [state, setState] = useState('idle');   // idle | saving | saved | error
    const save = async () => {
        if (!result && !note.trim()) return;
        if (result === 'Call Later' && !followUp) { alert('Call Later needs the promised date & time.'); return; }
        setState('saving');
        try {
            const j = await api('/api/tc/log', {
                mobile, customerName,
                result: result || 'Note', remark: note.trim(),
                outcomeOnly: true,
                followUpAt: followUp ? new Date(followUp).getTime() : 0,
                // Only the END-of-call outcome claims the call; a note typed while
                // talking must not stop the real outcome from being saved.
                ...(ended && liveCallKey ? { liveCallKey, device: 'web' } : {}),
            });
            if (j.ok && j.duplicate) { setState(j.savedBy === 'phone' ? 'dupPhone' : 'dup'); return; }
            setState(j.ok ? 'saved' : 'error');
            if (j.ok) { setNote(''); setResult(''); setFollowUp(''); setTimeout(() => setState('idle'), 2500); }
        } catch (e) { setState('error'); }
    };
    if (savedBy || state === 'dupPhone' || state === 'dup') {
        return (
            <div className="rounded-lg bg-green-50 border border-green-200 p-2.5 text-xs font-semibold text-green-800">
                {savedBy ? savedLabel(savedBy) : state === 'dupPhone' ? savedLabel('phone') : 'This call’s outcome was already saved ✓'}
                <span className="font-normal text-green-700"> — nothing more to log for this call.</span>
            </div>
        );
    }
    return (
        <div className="rounded-lg bg-slate-50 p-2.5 space-y-1.5">
            <div className="flex gap-1.5">
                <select value={result} onChange={e => setResult(e.target.value)}
                    className="flex-1 px-2 py-1.5 text-xs border border-slate-200 rounded-lg bg-white">
                    <option value="">{ended ? 'Outcome of the call…' : 'Outcome (set while talking)…'}</option>
                    {['Connected', 'Interested', 'Not Interested', 'Call Later', 'Ordered', 'Complaint Raised', 'Wrong Number', 'Note'].map(r => <option key={r}>{r}</option>)}
                </select>
                {result === 'Call Later' && (
                    <input type="datetime-local" value={followUp} onChange={e => setFollowUp(e.target.value)}
                        className="px-2 py-1.5 text-xs border border-slate-200 rounded-lg bg-white" />
                )}
            </div>
            <div className="flex gap-1.5">
                <input value={note} onChange={e => setNote(e.target.value)} placeholder="Remark / note for this call…"
                    onKeyDown={e => { if (e.key === 'Enter') save(); }}
                    className="flex-1 px-2 py-1.5 text-xs border border-slate-200 rounded-lg bg-white" />
                <button onClick={save} disabled={state === 'saving'}
                    className="px-3 py-1.5 rounded-lg bg-purple-600 text-white text-xs font-semibold hover:bg-purple-700 disabled:opacity-50">
                    {state === 'saving' ? '…' : state === 'saved' ? '✓ Saved' : 'Save'}
                </button>
            </div>
            {state === 'error' && <div className="text-[11px] text-red-600">Could not save — check the network and try again.</div>}
        </div>
    );
};

const LiveCallDock = () => {
    const navigate = useNavigate();
    const [live, setLive] = useState(null);          // liveCalls/{myUid}
    const [endedAt, setEndedAt] = useState(0);       // keep the panel briefly after idle
    const [profile, setProfile] = useState(null);
    const [profileErr, setProfileErr] = useState('');
    const [minimized, setMinimized] = useState(false);
    const [closed, setClosed] = useState(false);     // manual dismiss for THIS call
    // uid -> the call start that was dismissed, so a LATER call from the same agent
    // comes back on its own.
    const [dismissedTeam, setDismissedTeam] = useState({});
    const [busy, setBusy] = useState('');
    const [smsState, setSmsState] = useState('');   // '' | 'sent'
    const [smsNote, setSmsNote] = useState('');     // why it could not send, in words
    const [cmdNote, setCmdNote] = useState('');     // the phone's answer to attend / hang up
    const [, setTick] = useState(0);
    const lastMobile = useRef('');
    const lastCallAt = useRef(0);
    // The live entry is deleted the moment the call ends, taking its timings with it.
    // Keeping the final snapshot is what lets the ended card still say whether the
    // call was attended and for how long.
    const lastLive = useRef(null);
    // Caller 4.30: the proxy's richer record of the same call (tc/live/{uid}) — the
    // call's identity for the one-outcome rule, and whether the phone saved it.
    const { view: phoneView } = usePhonePresence();
    const tcLive = useLiveCall(!!phoneView && phoneView.status !== 'none');

    const [team, setTeam] = useState([]);     // other agents' active calls (managers only)
    const [isMgr, setIsMgr] = useState(false);
    useEffect(() => {
        api('/api/tc/me').then(j => setIsMgr(j?.isManager === true)).catch(() => {});
    }, []);
    useEffect(() => {
        const uid = auth.currentUser?.uid;
        if (!uid) return undefined;
        const unsub = onValue(ref(db, 'liveCalls'), (snap) => {
            const all = snap.exists() ? snap.val() : {};
            const v = all[uid] || null;
            if (v) lastLive.current = v;
            setLive(prev => {
                if (prev && !v) setEndedAt(Date.now());
                if (v && v.at !== lastCallAt.current) {
                    lastCallAt.current = v.at;
                    setClosed(false); setMinimized(false); setEndedAt(0);
                    if (v.mobile !== lastMobile.current) setProfile(null);
                }
                return v;
            });
            const now = Date.now();
            setTeam(Object.entries(all)
                .filter(([k, c]) => k !== uid && !liveCallStale(c, now))
                .map(([k, c]) => ({ uid: k, ...c })));
        }, () => { setLive(null); setTeam([]); });
        const t = setInterval(() => setTick(x => x + 1), 1000);
        return () => { unsub(); clearInterval(t); };
    }, []);

    const active = live && !liveCallStale(live);
    // The ended card stays while this call's outcome is still owed (up to 15 min), not
    // just 30 s — the agent may be finishing the outcome on whichever screen is nearer.
    const tcEnded = !!(tcLive && ['ended', 'missed'].includes(tcLive.state) && tcLive.mobile && tcLive.mobile === lastMobile.current);
    const tcOwed = tcEnded && !tcLive.cancelled && !(tcLive.outcome && tcLive.outcome.callKey === tcLive.callKey)
        && Date.now() - (Number(tcLive.endedAt) || 0) < OUTCOME_WAIT_MS;
    const showEnded = !active && lastMobile.current && ((endedAt && Date.now() - endedAt < 30_000) || tcOwed);
    const mobile = active ? live.mobile : lastMobile.current;
    const liveKeyForOutcome = tcEnded ? tcLive.callKey : '';
    const savedByOther = tcEnded && savedElsewhere(tcLive, tcLive.callKey, 'web') ? tcLive.outcome.by : '';
    // Saved on the phone: say so, then fold the ended card away by itself.
    useEffect(() => {
        if (!savedByOther || active) return undefined;
        const t = setTimeout(() => setClosed(true), 5000);
        return () => clearTimeout(t);
    }, [savedByOther, active]);

    const hasNumber = !!(mobile && String(mobile).length === 10);
    useEffect(() => {
        if (active && live.mobile) {
            lastMobile.current = live.mobile;
            if (!profile || profile.mobile !== live.mobile) {
                setProfileErr('');
                api(`/api/callapp/lookup?mobile=${live.mobile}`).then(j => {
                    if (j?.found) setProfile(j);
                    else if (j?.ok && !j.found) setProfileErr('Not a saved customer yet.');
                    else setProfileErr(j?.error || 'Profile unavailable — permissions or network.');
                }).catch(() => setProfileErr('Profile unavailable — network.'));
            }
        }
    }, [active, live?.mobile, live?.at]);

    // Team strip: live coordination visible on EVERY page, not just the workspace.
    // Each row is dismissible: these float over the bottom-right corner and were sitting
    // on top of the pagination buttons with no way to get rid of them. Dismissal is
    // remembered per agent AND per call start, so hiding one call does not blind the
    // manager to that agent's NEXT call.
    const visibleTeam = team.filter(c => !(dismissedTeam[c.uid] && dismissedTeam[c.uid] >= (c.at || 0)));
    const teamStrip = (isMgr && visibleTeam.length > 0) ? (
        <div className="space-y-1 mb-2">
            {visibleTeam.slice(0, 4).map(c => (
                <div key={c.uid} className={`flex items-center gap-2 rounded-xl px-3 py-1.5 text-xs shadow border bg-white ${c.state === 'in_call' ? 'border-green-200 text-green-800' : 'border-amber-200 text-amber-800'}`}>
                    <span className={`h-2 w-2 rounded-full ${c.state === 'in_call' ? 'bg-green-500' : 'bg-amber-400 animate-pulse'}`} />
                    <b>{c.agent || 'Agent'}</b>
                    <span className="text-slate-500 truncate">{c.state === 'in_call' ? 'in call with' : c.state} {c.name || c.mobile}</span>
                    <span className="ml-auto font-mono">{mmss(Date.now() - (c.at || Date.now()))}</span>
                    <button
                        type="button"
                        onClick={() => setDismissedTeam(d => ({ ...d, [c.uid]: c.at || Date.now() }))}
                        title="Hide this call"
                        aria-label={`Hide ${c.agent || 'agent'}'s call`}
                        className="shrink-0 -mr-1 px-1 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded"
                    >
                        ✕
                    </button>
                </div>
            ))}
        </div>
    ) : null;

    if ((!active && !showEnded) || closed) {
        return teamStrip ? <div className="fixed bottom-4 right-4 z-[90] w-[340px] max-w-[92vw]">{teamStrip}</div> : null;
    }

    const state = active ? live.state : 'ended';
    const dirOut = live?.direction === 'out';

    // Two clocks, not one. `at` is the last STATE CHANGE, so timing from it reset on
    // every transition: a call answered after 20 seconds of ringing showed 00:00.
    // The server now carries `startedAt` through the whole call and stamps
    // `answeredAt` the first time the phone reports a conversation — which is also
    // the only honest answer to "was it attended?".
    const src = active ? live : lastLive.current;
    const startedAt = Number(src?.startedAt) || Number(src?.at) || 0;
    const answeredAt = Number(src?.answeredAt) || 0;
    const attended = answeredAt > 0;
    const ringMs = startedAt ? (attended ? answeredAt - startedAt : Date.now() - startedAt) : 0;
    const talkMs = attended ? (active ? Date.now() - answeredAt : (endedAt || Date.now()) - answeredAt) : 0;

    const stateTxt = state === 'ringing' ? (dirOut ? `📞 Ringing the customer — ${mmss(ringMs)}` : `📳 Incoming — ringing ${mmss(ringMs)}`)
        : state === 'dialing' ? `📞 Dialing from your phone… ${mmss(ringMs)}`
        : state === 'in_call' ? `🟢 In call — ${mmss(talkMs)}`
        : attended ? `✔ Attended · talked ${mmss(talkMs)} — log the result`
        : `✖ Not attended · rang ${mmss(ringMs)} — log the result`;
    const name = profile?.name || live?.name || mobile;

    // Attend / hang up, confirmed BY THE PHONE. v3.11+ writes phoneCmds/{uid}/ack
    // with the command id and whether Android actually did it; without that the
    // desk only knew the server had accepted the request.
    const cmd = async (action) => {
        setBusy(action); setCmdNote('');
        try {
            const j = await api('/api/callapp/phonecmd', { action });
            if (!j.ok) { setCmdNote(j.error || 'Phone did not get the command'); return; }
            const uid = auth.currentUser?.uid;
            if (!j.id || !uid) return;
            const ack = await new Promise(resolve => {
                let done = false, unsub = null;
                const finish = (a) => { if (done) return; done = true; clearTimeout(timer); if (unsub) unsub(); resolve(a); };
                const timer = setTimeout(() => finish(null), 6000);
                unsub = onValue(ref(db, `phoneCmds/${uid}/ack`), s => {
                    const a = s.val();
                    if (a && a.id === j.id) finish(a);
                }, () => finish(null));
                if (done) unsub();   // answered synchronously from cache
            });
            if (!ack) setCmdNote(`The phone did not confirm the ${action === 'hangup' ? 'hang-up' : 'answer'} — use the phone itself. (Phones on an app older than v3.11 cannot confirm.)`);
            else if (!ack.ok) setCmdNote(`Phone could not ${action === 'hangup' ? 'hang up' : 'answer'}: ${ack.reason || 'unknown reason'}`);
        } finally { setBusy(''); }
    };

    const waText = encodeURIComponent(`Hi ${profile?.name || ''}, this is the Daksha Organics team 🌿`);

    /**
     * Text the customer we tried to reach them — the usual move after a call nobody
     * picked up. Indian SMS is DLT-template bound, so the gateway sends the approved
     * CALL_FOLLOWUP template rather than free text; if that template is not mapped
     * yet the server says so and the agent is told exactly what is missing instead of
     * watching a button do nothing.
     */
    const sendSms = async () => {
        if (!hasNumber) return;
        setBusy('sms'); setSmsNote('');
        try {
            const j = await api('/api/notifications/sms/send', {
                eventKey: 'CALL_FOLLOWUP', mobile,
                variables: { NAME: profile?.name || '' },
                relatedType: 'call', relatedId: String(startedAt || ''),
            });
            if (j.status === 'sent') { setSmsState('sent'); setSmsNote('Text sent'); }
            else if (j.status === 'skipped') setSmsNote('Already texted in the last 5 minutes');
            else if (j.status === 'not_configured') setSmsNote('SMS gateway is not configured on the server');
            else if (/No template mapped/i.test(j.errorMessage || '')) setSmsNote('No approved SMS template yet — map MSG91_TID_CALL_FOLLOWUP');
            else setSmsNote(j.errorMessage || 'Could not send the text');
        } finally { setBusy(''); }
    };

    return (
        // The dock grows with the customer's history, so on a laptop it used to run off
        // the top of the screen and take the status line and the HANG UP button with it.
        // It is now bounded by the viewport: the header and the call controls are
        // pinned, and only the context below them scrolls.
        <div className="fixed bottom-4 right-4 z-[90] w-[360px] max-w-[92vw] max-h-[calc(100vh-2rem)] flex flex-col">
        {teamStrip}
        <div className={`rounded-2xl shadow-2xl border bg-white overflow-hidden flex flex-col min-h-0 ${
            state === 'in_call' ? 'border-green-300' : state === 'ended' ? (attended ? 'border-slate-200' : 'border-red-200') : 'border-amber-300'}`}>
            {/* Header: live status + controls */}
            <div className={`shrink-0 px-4 py-2.5 flex items-center gap-2 text-sm font-semibold ${
                state === 'in_call' ? 'bg-green-50 text-green-800'
                    : state === 'ended' ? (attended ? 'bg-slate-50 text-slate-600' : 'bg-red-50 text-red-700')
                        : 'bg-amber-50 text-amber-800'}`}>
                <span className="truncate">{stateTxt}</span>
                <button onClick={() => setMinimized(m => !m)} className="ml-auto p-1 rounded hover:bg-black/5" title={minimized ? 'Expand' : 'Minimize'}><Minus size={14} /></button>
                <button onClick={() => setClosed(true)} className="p-1 rounded hover:bg-black/5" title="Hide"><X size={14} /></button>
            </div>

            {!minimized && (
                <>
                {/* Pinned: who is on the line and how to act on the call. These must
                    stay reachable no matter how much history is loaded below. */}
                <div className="shrink-0 p-4 pb-3 space-y-3 text-sm border-b border-slate-100">
                    {/* Who */}
                    <div>
                        <div className="font-bold text-slate-800 text-base">{name}</div>
                        <div className="text-xs text-slate-400 font-mono">{mobile}
                            {profile?.city ? <span className="font-sans"> · {profile.city}{profile.state ? `, ${profile.state}` : ''}</span> : null}
                        </div>
                        {(profile?.dnd || profile?.notInterested) && (
                            <div className="mt-1 text-xs font-bold text-red-600">
                                {profile.dnd ? '⛔ DND — asked not to be called ' : ''}{profile.notInterested ? '⚠ Marked not interested' : ''}
                            </div>
                        )}
                    </div>

                    {/* Phone remote control - 2-up grid, labels never wrap */}
                    <div className="grid grid-cols-2 gap-2">
                        {state === 'ringing' && !dirOut && (
                            <button onClick={() => cmd('answer')} disabled={!!busy}
                                className="flex items-center justify-center gap-1.5 rounded-xl bg-green-600 text-white py-2 text-sm font-semibold whitespace-nowrap hover:bg-green-700 disabled:opacity-50">
                                <PhoneIncoming size={15} /> {busy === 'answer' ? 'Attending…' : 'Attend'}
                            </button>
                        )}
                        {(state === 'ringing' || state === 'in_call' || state === 'dialing') && (
                            <button onClick={() => cmd('hangup')} disabled={!!busy}
                                className="flex items-center justify-center gap-1.5 rounded-xl bg-red-600 text-white py-2 text-sm font-semibold whitespace-nowrap hover:bg-red-700 disabled:opacity-50">
                                <PhoneOff size={15} /> {busy === 'hangup' ? 'Ending…' : 'Hang up'}
                            </button>
                        )}
                        {hasNumber && (
                            <button onClick={sendSms} disabled={busy === 'sms' || smsState === 'sent'}
                                title="Text this customer that we tried to reach them"
                                className="flex items-center justify-center gap-1.5 rounded-xl bg-sky-50 text-sky-700 py-2 text-sm font-semibold whitespace-nowrap hover:bg-sky-100 disabled:opacity-60">
                                <MessageSquare size={15} /> {busy === 'sms' ? 'Sending…' : smsState === 'sent' ? 'SMS sent' : 'Send SMS'}
                            </button>
                        )}
                        {hasNumber && <a href={`https://wa.me/91${mobile}?text=${waText}`} target="_blank" rel="noreferrer"
                            className="flex items-center justify-center gap-1.5 rounded-xl bg-emerald-50 text-emerald-700 py-2 text-sm font-semibold whitespace-nowrap hover:bg-emerald-100">
                            <MessageCircle size={15} /> WhatsApp
                        </a>}
                        {hasNumber && <button onClick={() => navigate(`/customers/${mobile}?addOrder=1`)}
                            className="flex items-center justify-center gap-1.5 rounded-xl bg-indigo-50 text-indigo-700 py-2 text-sm font-semibold whitespace-nowrap hover:bg-indigo-100" title="Open full profile / add order">
                            <User size={15} /> + Order
                        </button>}
                    </div>

                    {cmdNote && <div className="text-[11px] font-medium text-red-700">{cmdNote}</div>}
                    {smsNote && (
                        <div className={`text-[11px] font-medium ${smsState === 'sent' ? 'text-sky-700' : 'text-amber-700'}`}>{smsNote}</div>
                    )}
                </div>

                {/* Everything below scrolls inside the dock, never off the screen. */}
                <div className="flex-1 min-h-0 overflow-y-auto p-4 pt-3 space-y-3 text-sm">
                    {/* In-call note & outcome - saved against the call, right here */}
                    {hasNumber && <QuickOutcome mobile={mobile} customerName={profile?.name || live?.name || ''} ended={state === 'ended'}
                        liveCallKey={state === 'ended' ? liveKeyForOutcome : ''} savedBy={state === 'ended' ? savedByOther : ''} />}

                    {/* Profile */}
                    {!hasNumber ? (
                        <div className="text-xs text-slate-400">Identifying the caller — the customer card appears the moment the number comes through.</div>
                    ) : profile ? (
                        <div className="space-y-2 text-xs">
                            {/* Why they are probably calling, above everything else.
                                An agent who learns about an open complaint halfway
                                through the call has already lost the conversation. */}
                            {(profile.openTickets || []).length > 0 && (
                                <div className="rounded-lg border border-rose-200 bg-rose-50 p-2.5 space-y-1.5">
                                    <div className="font-bold text-rose-700 uppercase text-[10px] flex items-center gap-1">
                                        <LifeBuoy size={11} /> {profile.openTickets.length} open ticket{profile.openTickets.length > 1 ? 's' : ''}
                                        {profile.ticketCounts?.total > profile.openTickets.length && (
                                            <span className="font-semibold text-rose-400 normal-case"> · {profile.ticketCounts.total} all time</span>
                                        )}
                                    </div>
                                    {profile.openTickets.map(t => (
                                        <button key={t.id} type="button" onClick={() => navigate(`/complaints?ticket=${encodeURIComponent(t.id)}`)}
                                            className="w-full text-left rounded-md bg-white border border-rose-200 px-2 py-1.5 hover:border-rose-400 transition-colors">
                                            <div className="flex items-center gap-1.5">
                                                <span className="font-bold text-rose-700">{t.ticketNumber || 'Ticket'}</span>
                                                <span className={`px-1.5 rounded-full text-[10px] font-bold ${t.priority === 'high' || t.priority === 'urgent' ? 'bg-red-600 text-white' : 'bg-rose-100 text-rose-700'}`}>
                                                    {t.status}{t.priority ? ` · ${t.priority}` : ''}
                                                </span>
                                                {t.slaBreached && <span className="px-1.5 rounded-full text-[10px] font-bold bg-amber-500 text-white">SLA missed</span>}
                                                <ExternalLink size={11} className="ml-auto text-rose-400 shrink-0" />
                                            </div>
                                            <div className="truncate text-slate-700">{t.subject}</div>
                                            <div className="text-[11px] text-slate-400">
                                                {t.issueType ? `${t.issueType} · ` : ''}open {t.ageDays === 0 ? 'today' : `${t.ageDays}d`}
                                                {t.orderId ? ` · ${t.orderId}` : ''}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* A parcel still on the road — the other reason the phone rings. */}
                            {(profile.activeShipments || []).length > 0 && (
                                <div className="rounded-lg border border-sky-200 bg-sky-50 p-2.5 space-y-1.5">
                                    <div className="font-bold text-sky-700 uppercase text-[10px] flex items-center gap-1">
                                        <Truck size={11} /> {profile.activeShipments.length} shipment{profile.activeShipments.length > 1 ? 's' : ''} on the way
                                    </div>
                                    {/* Opens the profile: its order history carries the full
                                        journey. /tracking takes no deep-link parameter, so
                                        sending the agent there would do nothing. */}
                                    {profile.activeShipments.map(s => (
                                        <button key={s.orderKey} type="button" onClick={() => navigate(`/customers/${mobile}`)}
                                            className="w-full text-left rounded-md bg-white border border-sky-200 px-2 py-1.5 hover:border-sky-400 transition-colors">
                                            <div className="flex items-center gap-1.5">
                                                <span className="font-bold text-sky-800">{s.trackingStatus || s.orderStatus}</span>
                                                {s.staleDays !== null && s.staleDays >= 3 && (
                                                    <span className="px-1.5 rounded-full text-[10px] font-bold bg-amber-500 text-white">no scan {s.staleDays}d</span>
                                                )}
                                                <ExternalLink size={11} className="ml-auto text-sky-400 shrink-0" />
                                            </div>
                                            <div className="truncate text-slate-700">{s.orderId}{s.courier ? ` · ${s.courier}` : ''}{s.total ? ` · ₹${s.total}` : ''}{s.paymentMode ? ` · ${s.paymentMode}` : ''}</div>
                                            <div className="text-[11px] text-slate-400 font-mono truncate">
                                                {s.awb || 'awaiting AWB'}
                                                {s.lastUpdate ? <span className="font-sans"> · last scan {fmtNiceDate(String(s.lastUpdate).slice(0, 10))}</span> : null}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}

                            <div className="rounded-lg bg-slate-50 p-2.5 space-y-1">
                                <div>
                                    <b className="text-slate-700">{profile.totalOrders} order(s) · ₹{Number(profile.totalSpent).toLocaleString()} lifetime</b>
                                    <span className="text-slate-500">
                                        {profile.customerType ? ` · ${profile.customerType}` : ''}
                                        {profile.churnRisk ? ` · ${profile.churnRisk} churn` : ''}
                                    </span>
                                </div>
                                {(profile.recentOrders || [])[0] && (
                                    <div className="rounded-md bg-white border border-slate-200 px-2 py-1.5">
                                        <div className="text-[10px] font-bold text-slate-400 uppercase">Last order · {fmtNiceDate(profile.recentOrders[0].date)}</div>
                                        <div className="flex justify-between gap-2">
                                            <span className="truncate font-medium text-slate-700">{profile.recentOrders[0].products || profile.recentOrders[0].orderId}</span>
                                            <span className="shrink-0 font-bold text-slate-700">₹{profile.recentOrders[0].total}</span>
                                        </div>
                                        <div className="text-[11px] text-slate-400">{profile.recentOrders[0].orderId} · {profile.recentOrders[0].paymentMode} · {profile.recentOrders[0].status || '—'}</div>
                                    </div>
                                )}
                                {(profile.favoriteProduct || profile.preferredPayment) && (
                                    <div className="text-slate-500">Prefers: {[profile.favoriteProduct, profile.preferredPayment && `${profile.preferredPayment} payments`].filter(Boolean).join(' · ')}</div>
                                )}
                            </div>
                            {(profile.recentOrders || []).length > 1 && (
                                <div className="rounded-lg bg-slate-50 p-2.5 space-y-2">
                                    <div className="font-bold text-slate-400 uppercase text-[10px]">Earlier orders</div>
                                    {profile.recentOrders.slice(1).map(o => (
                                        <div key={o.orderId} className="leading-tight">
                                            <div className="flex justify-between gap-2">
                                                <span className="truncate text-slate-600">{o.products || o.orderId}</span>
                                                <span className="shrink-0 font-semibold text-slate-600">₹{o.total}</span>
                                            </div>
                                            <div className="text-[11px] text-slate-400">{fmtNiceDate(o.date)} · {o.orderId} · {o.status || '—'}</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {profile.lastRemark && (
                                <div className="rounded-lg bg-amber-50 p-2.5 text-amber-800">💬 “{profile.lastRemark}”</div>
                            )}
                            {profile.lastCall?.calledAt ? (
                                <div className="text-slate-400">
                                    Last call: {new Date(profile.lastCall.calledAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })} by {profile.lastCall.calledBy || 'team'} ({profile.lastCall.result})
                                </div>
                            ) : null}
                        </div>
                    ) : profileErr ? (
                        <div className="text-xs text-amber-700">⚠ {profileErr}</div>
                    ) : (
                        <div className="text-xs text-slate-400">Loading customer profile…</div>
                    )}
                </div>
                </>
            )}
        </div>
        </div>
    );
};

export default LiveCallDock;
